import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useStore } from '../context/StoreContext';
import { useToast } from '../context/ToastContext';
import { formatBRL, formatCPFInput, formatCEPInput, formatPhoneInput } from '../utils/format';
import {
  ShieldCheck,
  Lock,
  CreditCard,
  QrCode,
  CheckCircle2,
  ArrowRight,
  Info,
} from 'lucide-react';

interface CheckoutPageProps {
  onNavigate: (path: string) => void;
}

export function CheckoutPage({ onNavigate }: CheckoutPageProps) {
  const { user, login, register } = useAuth();
  const { items, subtotalCents, refreshCart } = useCart();
  const { settings } = useStore();
  const { showToast } = useToast();

  // Auth form states (if guest)
  const [authMode, setAuthMode] = useState<'login' | 'register'>('register');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authFullName, setAuthFullName] = useState('');
  const [authPhone, setAuthPhone] = useState('');
  const [authCpf, setAuthCpf] = useState('');
  const [authTotp, setAuthTotp] = useState('');
  const [requiresTotp, setRequiresTotp] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);

  // Address & Order Form State
  const [recipientName, setRecipientName] = useState('');
  const [phone, setPhone] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [street, setStreet] = useState('');
  const [number, setNumber] = useState('');
  const [complement, setComplement] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('SP');
  const [instructions, setInstructions] = useState('');

  // Payment selection state
  const [paymentMethod, setPaymentMethod] = useState<'pix' | 'credit_card' | 'test'>('pix');
  const [testOutcome, setTestOutcome] = useState<'approved' | 'pending' | 'rejected'>('approved');

  // Idempotency key generated once per checkout session
  const [idempotencyKey] = useState(() => `ord_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`);

  // Review calculation from server
  const [serverReview, setServerReview] = useState<{
    subtotal_cents: number;
    shipping_cents: number;
    total_cents: number;
    items: any[];
  } | null>(null);
  const [submittingOrder, setSubmittingOrder] = useState(false);

  // Pre-fill fields if user is already logged in
  useEffect(() => {
    if (user) {
      if (user.full_name) setRecipientName(user.full_name);
      if (user.phone) setPhone(user.phone);
    }
  }, [user]);

  // Request server order review to verify catalog prices & stock
  useEffect(() => {
    if (items.length === 0) return;

    fetch('/api/checkout/review', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: items.map((i) => ({ variant_id: i.variant_id, quantity: i.quantity })),
        shipping_zip: postalCode,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Não foi possível verificar os itens.');
        return res.json();
      })
      .then((data: any) => {
        setServerReview(data);
      })
      .catch((err) => {
        showToast('error', 'Aviso de Catálogo', err.message);
      });
  }, [items, postalCode, showToast]);

  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);

    if (authMode === 'login') {
      const res = await login(authEmail, authPassword, authTotp || undefined);
      if (res.requiresTotp) {
        setRequiresTotp(true);
        showToast('info', 'Código MFA Requerido', 'Digite o código do autenticador para prosseguir.');
      } else if (!res.success) {
        showToast('error', 'Falha no login', res.error);
      } else {
        showToast('success', 'Identificação realizada', 'Bem-vindo de volta.');
      }
    } else {
      const res = await register({
        email: authEmail,
        password: authPassword,
        full_name: authFullName,
        phone: authPhone,
        cpf: authCpf,
      });

      if (!res.success) {
        showToast('error', 'Falha no cadastro', res.error);
      } else {
        showToast('success', 'Conta criada', 'Seu cadastro foi realizado com sucesso.');
      }
    }
    setAuthLoading(false);
  };

  const hasPhysicalProducts = items.some((i) => i.product_type === 'physical');

  const handleFinalizeOrder = async () => {
    if (!user) {
      showToast('error', 'Autenticação necessária', 'Por favor, identifique-se para continuar.');
      return;
    }

    if (hasPhysicalProducts && (!street || !number || !city || !state || !postalCode)) {
      showToast('error', 'Endereço incompleto', 'Preencha todos os campos obrigatórios de entrega.');
      return;
    }

    setSubmittingOrder(true);
    try {
      // 1. Submit Order to D1 with idempotency key
      const orderPayload = {
        idempotency_key: idempotencyKey,
        items: items.map((i) => ({ variant_id: i.variant_id, quantity: i.quantity })),
        shipping_address: hasPhysicalProducts
          ? {
              recipient_name: recipientName || user.full_name,
              street,
              number,
              complement,
              neighborhood,
              city,
              state,
              postal_code: postalCode,
              phone: phone || user.phone,
            }
          : undefined,
        customer_instructions: instructions,
      };

      const orderRes = await fetch('/api/checkout/order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderPayload),
      });

      const orderData = (await orderRes.json()) as any;
      if (!orderRes.ok) {
        throw new Error(orderData.error || 'Falha ao gerar o pedido.');
      }

      const createdOrder = orderData.order;

      // 2. Process Payment via Isolated Provider
      const paymentRes = await fetch('/api/payments/process', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: createdOrder.id,
          method: paymentMethod,
          simulated_outcome: testOutcome,
        }),
      });

      const paymentData = (await paymentRes.json()) as any;
      if (!paymentRes.ok) {
        throw new Error(paymentData.error || 'Falha no processamento do pagamento.');
      }

      // Refresh cart to clear completed items
      await refreshCart();

      // Navigate to order confirmation
      onNavigate(`/pedido/${createdOrder.order_number}`);
    } catch (err: unknown) {
      showToast('error', 'Erro ao finalizar', err instanceof Error ? err.message : 'Ocorreu um erro.');
    } finally {
      setSubmittingOrder(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center space-y-4">
        <h1 className="text-2xl font-serif font-medium text-ink">Sua sacola está vazia</h1>
        <p className="text-xs text-ink-muted">Adicione produtos antes de prosseguir para o checkout.</p>
        <button
          onClick={() => onNavigate('/catalogo')}
          className="bg-ink text-ink-inverted px-6 py-2.5 rounded-xs text-xs uppercase tracking-wider font-medium hover:bg-primary transition-colors"
        >
          Voltar ao Catálogo
        </button>
      </div>
    );
  }

  const effectiveSubtotal = serverReview?.subtotal_cents ?? subtotalCents;
  const effectiveShipping = serverReview?.shipping_cents ?? (hasPhysicalProducts ? (settings.shipping_flat_rate_cents || 2800) : 0);
  const effectiveTotal = effectiveSubtotal + effectiveShipping;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
      {/* Header */}
      <div className="border-b border-border-subtle pb-6 flex items-center justify-between">
        <div>
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-primary">
            Ambiente Seguro
          </span>
          <h1 className="text-3xl font-serif font-normal text-ink mt-0.5">
            Finalização do Pedido
          </h1>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-olive font-medium bg-olive-light px-3 py-1.5 rounded-xs border border-olive/20">
          <Lock className="w-3.5 h-3.5" />
          <span>Criptografia AES-256-GCM</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Left Form: Authentication or Shipping/Payment */}
        <div className="lg:col-span-7 space-y-8">
          {/* STEP 1: IDENTIFICATION (IF GUEST) */}
          {!user ? (
            <div className="bg-surface p-6 rounded-xs border border-border-subtle shadow-subtle space-y-6">
              <div className="flex items-center justify-between border-b border-border-subtle pb-4">
                <h2 className="font-serif text-xl font-medium text-ink flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-ink text-ink-inverted text-xs flex items-center justify-center font-mono">
                    1
                  </span>
                  Identificação do Comprador
                </h2>
                <div className="flex gap-2 text-xs">
                  <button
                    onClick={() => {
                      setAuthMode('register');
                      setRequiresTotp(false);
                    }}
                    className={`px-3 py-1 rounded-xs transition-colors ${
                      authMode === 'register' ? 'bg-primary text-white font-medium' : 'text-ink-muted hover:text-ink'
                    }`}
                  >
                    Criar Conta
                  </button>
                  <button
                    onClick={() => setAuthMode('login')}
                    className={`px-3 py-1 rounded-xs transition-colors ${
                      authMode === 'login' ? 'bg-primary text-white font-medium' : 'text-ink-muted hover:text-ink'
                    }`}
                  >
                    Já Tenho Conta
                  </button>
                </div>
              </div>

              <form onSubmit={handleAuthSubmit} className="space-y-4">
                {authMode === 'register' && (
                  <>
                    <div>
                      <label className="text-xs uppercase tracking-wider font-semibold text-ink block mb-1">
                        Nome Completo *
                      </label>
                      <input
                        type="text"
                        required
                        value={authFullName}
                        onChange={(e) => setAuthFullName(e.target.value)}
                        placeholder="Nome e Sobrenome"
                        className="w-full bg-background border border-border-subtle px-3 py-2.5 text-xs rounded-xs focus:outline-none focus:border-primary"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs uppercase tracking-wider font-semibold text-ink block mb-1">
                          CPF (Obrigatório p/ Nota Fiscal) *
                        </label>
                        <input
                          type="text"
                          required
                          value={authCpf}
                          onChange={(e) => setAuthCpf(formatCPFInput(e.target.value))}
                          placeholder="000.000.000-00"
                          className="w-full bg-background border border-border-subtle px-3 py-2.5 text-xs rounded-xs focus:outline-none focus:border-primary font-mono"
                        />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-wider font-semibold text-ink block mb-1">
                          Telefone / WhatsApp
                        </label>
                        <input
                          type="text"
                          value={authPhone}
                          onChange={(e) => setAuthPhone(formatPhoneInput(e.target.value))}
                          placeholder="(11) 98765-4321"
                          className="w-full bg-background border border-border-subtle px-3 py-2.5 text-xs rounded-xs focus:outline-none focus:border-primary font-mono"
                        />
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-ink block mb-1">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    required
                    value={authEmail}
                    onChange={(e) => setAuthEmail(e.target.value)}
                    placeholder="seu@email.com"
                    className="w-full bg-background border border-border-subtle px-3 py-2.5 text-xs rounded-xs focus:outline-none focus:border-primary"
                  />
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-ink block mb-1">
                    Senha *
                  </label>
                  <input
                    type="password"
                    required
                    value={authPassword}
                    onChange={(e) => setAuthPassword(e.target.value)}
                    placeholder="Mínimo 8 caracteres"
                    className="w-full bg-background border border-border-subtle px-3 py-2.5 text-xs rounded-xs focus:outline-none focus:border-primary"
                  />
                </div>

                {requiresTotp && (
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-primary block mb-1">
                      Código de Autenticação em 2 Etapas (MFA)
                    </label>
                    <input
                      type="text"
                      required
                      maxLength={6}
                      value={authTotp}
                      onChange={(e) => setAuthTotp(e.target.value.trim())}
                      placeholder="000000"
                      className="w-full bg-background border-2 border-primary px-3 py-2.5 text-xs rounded-xs focus:outline-none font-mono text-center tracking-widest text-base"
                    />
                  </div>
                )}

                <button
                  type="submit"
                  disabled={authLoading}
                  className="w-full bg-ink text-ink-inverted py-3 rounded-xs text-xs uppercase tracking-widest font-medium hover:bg-primary transition-colors disabled:opacity-40"
                >
                  {authLoading ? 'Processando...' : authMode === 'register' ? 'Criar Conta e Prosseguir' : 'Entrar e Prosseguir'}
                </button>

                <p className="text-[11px] text-ink-muted leading-relaxed text-center">
                  Seus dados pessoais são armazenados sob criptografia de ponta a ponta (AES-256) em conformidade com a LGPD.
                </p>
              </form>
            </div>
          ) : (
            <div className="bg-surface p-4 rounded-xs border border-border-subtle flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase tracking-wider font-medium text-olive flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Identificado como
                </span>
                <p className="font-serif text-base font-medium text-ink">{user.full_name || user.email}</p>
                <p className="text-xs text-ink-muted">{user.email}</p>
              </div>
              <span className="text-xs text-ink-muted bg-surface-muted px-2.5 py-1 rounded-xs">
                Sessão Ativa
              </span>
            </div>
          )}

          {/* STEP 2: SHIPPING DETAILS (IF PHYSICAL GOODS IN CART) */}
          {user && hasPhysicalProducts && (
            <div className="bg-surface p-6 rounded-xs border border-border-subtle shadow-subtle space-y-6">
              <h2 className="font-serif text-xl font-medium text-ink flex items-center gap-2 border-b border-border-subtle pb-4">
                <span className="w-6 h-6 rounded-full bg-ink text-ink-inverted text-xs flex items-center justify-center font-mono">
                  2
                </span>
                Endereço de Entrega
              </h2>

              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-ink block mb-1">
                      Destinatário *
                    </label>
                    <input
                      type="text"
                      required
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      placeholder="Nome de quem vai receber"
                      className="w-full bg-background border border-border-subtle px-3 py-2 text-xs rounded-xs focus:outline-none focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-ink block mb-1">
                      CEP *
                    </label>
                    <input
                      type="text"
                      required
                      value={postalCode}
                      onChange={(e) => setPostalCode(formatCEPInput(e.target.value))}
                      placeholder="00000-000"
                      className="w-full bg-background border border-border-subtle px-3 py-2 text-xs rounded-xs focus:outline-none focus:border-primary font-mono"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                  <div className="sm:col-span-3">
                    <label className="text-xs uppercase tracking-wider font-semibold text-ink block mb-1">
                      Logradouro (Rua / Avenida) *
                    </label>
                    <input
                      type="text"
                      required
                      value={street}
                      onChange={(e) => setStreet(e.target.value)}
                      placeholder="Ex: Rua Harmonia"
                      className="w-full bg-background border border-border-subtle px-3 py-2 text-xs rounded-xs focus:outline-none focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-ink block mb-1">
                      Número *
                    </label>
                    <input
                      type="text"
                      required
                      value={number}
                      onChange={(e) => setNumber(e.target.value)}
                      placeholder="123"
                      className="w-full bg-background border border-border-subtle px-3 py-2 text-xs rounded-xs focus:outline-none focus:border-primary font-mono"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-ink block mb-1">
                      Complemento
                    </label>
                    <input
                      type="text"
                      value={complement}
                      onChange={(e) => setComplement(e.target.value)}
                      placeholder="Apto 42, Bloco B"
                      className="w-full bg-background border border-border-subtle px-3 py-2 text-xs rounded-xs focus:outline-none focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-ink block mb-1">
                      Bairro *
                    </label>
                    <input
                      type="text"
                      required
                      value={neighborhood}
                      onChange={(e) => setNeighborhood(e.target.value)}
                      placeholder="Ex: Vila Madalena"
                      className="w-full bg-background border border-border-subtle px-3 py-2 text-xs rounded-xs focus:outline-none focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-ink block mb-1">
                      Cidade / UF *
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        required
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        placeholder="São Paulo"
                        className="flex-1 bg-background border border-border-subtle px-3 py-2 text-xs rounded-xs focus:outline-none focus:border-primary"
                      />
                      <input
                        type="text"
                        required
                        maxLength={2}
                        value={state}
                        onChange={(e) => setState(e.target.value.toUpperCase())}
                        placeholder="SP"
                        className="w-12 bg-background border border-border-subtle px-2 py-2 text-xs rounded-xs focus:outline-none focus:border-primary font-mono text-center"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wider font-semibold text-ink block mb-1">
                    Instruções de Entrega ou Observações (Opcional)
                  </label>
                  <textarea
                    rows={2}
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    placeholder="Instruções para o entregador, pontos de referência ou observações da encomenda..."
                    className="w-full bg-background border border-border-subtle p-3 text-xs rounded-xs focus:outline-none focus:border-primary"
                  />
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: PAYMENT & CONFIRMATION */}
          {user && (
            <div className="bg-surface p-6 rounded-xs border border-border-subtle shadow-subtle space-y-6">
              <h2 className="font-serif text-xl font-medium text-ink flex items-center gap-2 border-b border-border-subtle pb-4">
                <span className="w-6 h-6 rounded-full bg-ink text-ink-inverted text-xs flex items-center justify-center font-mono">
                  {hasPhysicalProducts ? '3' : '2'}
                </span>
                Forma de Pagamento
              </h2>

              {/* Notice regarding Mercado Pago & Test Mode */}
              <div className="bg-background-alt border border-border-subtle p-4 rounded-xs text-xs space-y-2">
                <div className="flex items-center gap-2 font-medium text-ink">
                  <Info className="w-4 h-4 text-primary shrink-0" />
                  <span>Ambiente de Verificação & Simulação de Pagamento</span>
                </div>
                <p className="text-ink-muted leading-relaxed">
                  A integração de produção com <strong>Mercado Pago</strong> está devidamente estruturada e aguarda a inserção das credenciais reais de produção. Para validar o ciclo completo de compra, reserva de estoque e liberação de downloads, você pode utilizar o simulador determinístico abaixo.
                </p>
              </div>

              {/* Payment Method Selector */}
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setPaymentMethod('pix')}
                  className={`p-4 border rounded-xs text-left transition-all ${
                    paymentMethod === 'pix'
                      ? 'border-primary bg-primary-subtle ring-1 ring-primary'
                      : 'border-border-subtle hover:border-ink/20'
                  }`}
                >
                  <QrCode className="w-5 h-5 text-primary mb-2" />
                  <p className="text-xs font-semibold text-ink">PIX (Instantâneo)</p>
                  <p className="text-[11px] text-ink-muted mt-0.5">Aprovação imediata via QR Code</p>
                </button>

                <button
                  type="button"
                  onClick={() => setPaymentMethod('credit_card')}
                  className={`p-4 border rounded-xs text-left transition-all ${
                    paymentMethod === 'credit_card'
                      ? 'border-primary bg-primary-subtle ring-1 ring-primary'
                      : 'border-border-subtle hover:border-ink/20'
                  }`}
                >
                  <CreditCard className="w-5 h-5 text-primary mb-2" />
                  <p className="text-xs font-semibold text-ink">Cartão de Crédito</p>
                  <p className="text-[11px] text-ink-muted mt-0.5">Em até 3x sem juros</p>
                </button>
              </div>

              {/* Simulation outcome picker for testing */}
              <div className="border border-border-subtle p-3.5 rounded-xs bg-surface space-y-2">
                <label className="text-[11px] uppercase tracking-wider font-semibold text-ink-muted block">
                  Simulação do Provedor de Pagamento (Ambiente de Teste)
                </label>
                <div className="flex gap-3 text-xs">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="testOutcome"
                      checked={testOutcome === 'approved'}
                      onChange={() => setTestOutcome('approved')}
                      className="text-primary focus:ring-primary"
                    />
                    <span>Aprovar Imediatamente</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="testOutcome"
                      checked={testOutcome === 'pending'}
                      onChange={() => setTestOutcome('pending')}
                      className="text-primary focus:ring-primary"
                    />
                    <span>Pendente (Gerar PIX)</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="testOutcome"
                      checked={testOutcome === 'rejected'}
                      onChange={() => setTestOutcome('rejected')}
                      className="text-primary focus:ring-primary"
                    />
                    <span>Simular Rejeição</span>
                  </label>
                </div>
              </div>

              {/* Final CTA Button */}
              <button
                type="button"
                onClick={handleFinalizeOrder}
                disabled={submittingOrder}
                className="w-full bg-ink text-ink-inverted py-4 px-6 rounded-xs text-xs uppercase tracking-widest font-medium hover:bg-primary transition-all flex items-center justify-center gap-2 group shadow-elevated disabled:opacity-40"
              >
                {submittingOrder ? (
                  'Validando estoque e processando...'
                ) : (
                  <>
                    Confirmar Pedido &bull; {formatBRL(effectiveTotal)}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Right Summary: Verified Order Items & Pricing */}
        <div className="lg:col-span-5">
          <div className="bg-surface p-6 rounded-xs border border-border-subtle shadow-subtle space-y-6 sticky top-28">
            <h2 className="font-serif text-lg font-medium text-ink border-b border-border-subtle pb-3">
              Itens do Pedido ({items.length})
            </h2>

            <div className="divide-y divide-border-subtle max-h-96 overflow-y-auto pr-1 space-y-3">
              {items.map((item) => (
                <div key={item.cart_item_id} className="pt-3 first:pt-0 flex gap-3 items-center">
                  <img
                    src={item.cover_image || 'https://images.unsplash.com/photo-1612196808214-b8e1d6145a8c?auto=format&fit=crop&w=150&q=80'}
                    alt={item.product_title}
                    className="w-14 h-16 object-cover rounded-xs bg-background-alt shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-serif font-medium text-ink line-clamp-1">
                      {item.product_title}
                    </p>
                    <p className="text-[11px] text-ink-muted">{item.variant_title}</p>
                    <p className="text-[11px] text-ink-muted">Qtd: {item.quantity}</p>
                  </div>
                  <span className="font-mono text-xs font-medium text-ink">
                    {formatBRL(item.price_cents * item.quantity)}
                  </span>
                </div>
              ))}
            </div>

            {/* Calculations Breakdown */}
            <div className="pt-4 border-t border-border-subtle space-y-2 text-xs">
              <div className="flex justify-between text-ink-muted">
                <span>Subtotal dos Produtos</span>
                <span className="font-mono text-ink">{formatBRL(effectiveSubtotal)}</span>
              </div>
              <div className="flex justify-between text-ink-muted">
                <span>Frete & Manuseio</span>
                <span className="font-mono text-ink">
                  {effectiveShipping === 0 ? <span className="text-olive font-medium">Grátis</span> : formatBRL(effectiveShipping)}
                </span>
              </div>
              <div className="pt-3 border-t border-border-subtle flex justify-between items-baseline">
                <span className="text-sm font-medium text-ink">Total a Pagar</span>
                <span className="font-mono text-xl font-medium text-ink">
                  {formatBRL(effectiveTotal)}
                </span>
              </div>
            </div>

            <div className="pt-2 text-[11px] text-ink-muted space-y-1 border-t border-border-subtle">
              <p className="flex items-center gap-1 text-olive font-medium">
                <ShieldCheck className="w-3.5 h-3.5" /> Servidor Oficial Aura Ateliê
              </p>
              <p>Chave de idempotência ativa para prevenção de cobrança duplicada.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
